/**
 * DosimetryStateService — single source of truth for dosimetry planning state.
 *
 * Manages:
 * - Perfused volumes (user-defined segment/tumor selections)
 * - Adjusted dose values per perfused volume
 * - Reference dose tables keyed by perfused volume ID (from API results)
 * - Simulated TEP volumes (vtkImageData) keyed by perfused volume ID
 *
 * Broadcasts PubSub events on every mutation to drive UI updates and auto-save.
 * VTK resources (TEP volumes) are explicitly deleted on removal to prevent memory leaks.
 */

import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';

import type { IDoseTable } from '../domain/dose-table';
import type { IPerfusedVolume } from '../domain/perfused-volume';
import { getSmartName, computeHepaticReserve } from '../domain/perfused-volume';
import { generatePvId } from '../domain/pv-id';
import type { TumorSegmentOverlaps } from '../domain/volume-computation';

import { PubSubService } from '@/visualization/core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '@/visualization/core/types';

// ============================================================================
// Persisted State DTO
// ============================================================================

export interface IDosimetryStateDTO {
    readonly version: 1;
    readonly perfusedVolumes: readonly IPerfusedVolume[];
    /** perfusedVolumeId -> adjusted injected activity */
    readonly adjustedDoses: Readonly<Record<string, { injectedActivityGBq: number }>>;
}

// ============================================================================
// Domain Types
// ============================================================================

export interface SegmentEntry {
    labelValue: number;
    name: string;
    volumeMl: number;
}

// ============================================================================
// Service
// ============================================================================

export class DosimetryStateService extends PubSubService {
    static readonly SERVICE_NAME = 'DosimetryStateService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: DosimetryStateService.SERVICE_NAME,
        factory: (context: ServiceContext) => new DosimetryStateService(context),
    };

    readonly EVENTS = {
        INITIALIZED: 'dosimetry-state:initialized',
        PERFUSED_VOLUME_ADDED: 'dosimetry-state:pv:added',
        PERFUSED_VOLUME_REMOVED: 'dosimetry-state:pv:removed',
        DOSE_ADJUSTED: 'dosimetry-state:dose:adjusted',
        DOSE_TABLE_LOADED: 'dosimetry-state:dose-table:loaded',
        SELECTED_PV_CHANGED: 'dosimetry-state:selected-pv:changed',
        HIGHLIGHT_CHANGED: 'dosimetry-state:highlight:changed',
        TEP_VOLUME_LOADED: 'dosimetry-state:tep-volume:loaded',
        TEP_VOLUME_REMOVED: 'dosimetry-state:tep-volume:removed',
    } as const;

    /** Events that represent user mutations (trigger persistence). */
    get MUTATION_EVENTS(): string[] {
        return [
            this.EVENTS.PERFUSED_VOLUME_ADDED,
            this.EVENTS.PERFUSED_VOLUME_REMOVED,
            this.EVENTS.DOSE_ADJUSTED,
        ];
    }

    get ALL_EVENTS(): string[] {
        return [
            ...this.MUTATION_EVENTS,
            this.EVENTS.INITIALIZED,
            this.EVENTS.DOSE_TABLE_LOADED,
            this.EVENTS.SELECTED_PV_CHANGED,
            this.EVENTS.HIGHLIGHT_CHANGED,
            this.EVENTS.TEP_VOLUME_LOADED,
            this.EVENTS.TEP_VOLUME_REMOVED,
        ];
    }

    // ── State ──
    private _initialized = false;
    private _perfusedVolumes: IPerfusedVolume[] = [];
    private _adjustedDoses: Record<string, { injectedActivityGBq: number }> = {};
    private _selectedPvId: string | null = null;
    /** Reference dose tables keyed by perfused volume ID (from API, at reference 2 GBq) */
    private _doseTables: Record<string, IDoseTable> = {};
    /** Simulated TEP volumes keyed by perfused volume ID */
    private _tepVolumes: Record<string, vtkImageData> = {};

    /** Segment volumes (mL) keyed by labelValue — set by workflow service after loading */
    private _segmentVolumes: ReadonlyMap<number, number> = new Map();
    /** Tumor volumes (mL) keyed by labelValue */
    private _tumorVolumes: ReadonlyMap<number, number> = new Map();
    /** Overlap volumes: tumorLabel → segmentLabel → overlap mL */
    private _tumorSegmentOverlaps: TumorSegmentOverlaps = new Map();

    /** Structure currently under the mouse pointer (at most one). */
    private _hoveredStructure: string | null = null;
    /** Structures actively selected (PV contents or in-progress checkbox selection). */
    private _selectedStructures: ReadonlySet<string> = new Set();

    constructor(_context: ServiceContext) {
        super();
    }

    // ========================================================================
    // Lifecycle
    // ========================================================================

    /**
     * Initialize with computed volumes from the workflow service.
     */
    initialize(
        segmentVolumes: ReadonlyMap<number, number>,
        tumorVolumes: ReadonlyMap<number, number>,
        tumorSegmentOverlaps?: TumorSegmentOverlaps
    ): void {
        this._segmentVolumes = segmentVolumes;
        this._tumorVolumes = tumorVolumes;
        this._tumorSegmentOverlaps = tumorSegmentOverlaps ?? new Map();
        this._initialized = true;
        this.broadcast(this.EVENTS.INITIALIZED);
    }

    isInitialized(): boolean {
        return this._initialized;
    }

    // ========================================================================
    // Computed Helpers
    // ========================================================================

    getTotalLiverVolumeMl(): number {
        let total = 0;
        for (const vol of this._segmentVolumes.values()) {
            total += vol;
        }
        return total;
    }

    private computeSegmentVolumeMl(segmentIndices: readonly number[]): number {
        let total = 0;
        for (const idx of segmentIndices) {
            total += this._segmentVolumes.get(idx) ?? 0;
        }
        return total;
    }

    private computeTumorVolumeMl(tumorIndices: readonly number[]): number {
        let total = 0;
        for (const idx of tumorIndices) {
            total += this._tumorVolumes.get(idx) ?? 0;
        }
        return total;
    }

    /**
     * Compute the union volume of selected segments and tumors.
     *
     * Union = sum(segmentVols) + sum(tumorVols) - sum(overlaps for each selected tumor-segment pair)
     *
     * This avoids double-counting tumor voxels that fall inside selected segments.
     */
    computeUnionVolumeMl(segmentIndices: readonly number[], tumorIndices: readonly number[]): number {
        const segVol = this.computeSegmentVolumeMl(segmentIndices);
        const tumVol = this.computeTumorVolumeMl(tumorIndices);

        let overlap = 0;
        for (const t of tumorIndices) {
            const segOverlaps = this._tumorSegmentOverlaps.get(t);
            if (!segOverlaps) continue;
            for (const s of segmentIndices) {
                overlap += segOverlaps.get(s) ?? 0;
            }
        }

        return segVol + tumVol - overlap;
    }

    // ========================================================================
    // Perfused Volume Mutations
    // ========================================================================

    addPerfusedVolume(segmentIndices: number[], tumorIndices: number[], name?: string): IPerfusedVolume {
        const id = generatePvId();
        const sortedSegs = [...segmentIndices].sort((a, b) => a - b);
        const sortedTums = [...tumorIndices].sort((a, b) => a - b);
        const volumeMl = this.computeUnionVolumeMl(sortedSegs, sortedTums);
        const tumorVolumeMl = this.computeTumorVolumeMl(sortedTums);
        const totalLiver = this.getTotalLiverVolumeMl();

        const pv: IPerfusedVolume = {
            id,
            name: name ?? getSmartName(segmentIndices),
            segmentIndices: sortedSegs,
            tumorIndices: sortedTums,
            volumeMl,
            tumorVolumeMl,
            hepaticReservePercent: computeHepaticReserve(totalLiver, volumeMl),
        };

        this._perfusedVolumes = [...this._perfusedVolumes, pv];

        if (this._selectedPvId === null) {
            this._selectedPvId = id;
            this.syncSelectedStructuresFromPV();
        }

        this.broadcast(this.EVENTS.PERFUSED_VOLUME_ADDED, { id });
        return pv;
    }

    removePerfusedVolume(id: string): void {
        const prev = this._perfusedVolumes;
        this._perfusedVolumes = prev.filter((pv) => pv.id !== id);
        if (this._perfusedVolumes.length === prev.length) return;

        // Clean up adjusted dose, dose table, and TEP volume
        delete this._adjustedDoses[id];
        delete this._doseTables[id];
        if (id in this._tepVolumes) {
            this._tepVolumes[id].delete();
            delete this._tepVolumes[id];
            this.broadcast(this.EVENTS.TEP_VOLUME_REMOVED, { pvId: id });
        }

        // Update selection
        if (this._selectedPvId === id) {
            this._selectedPvId = this._perfusedVolumes[0]?.id ?? null;
            this.syncSelectedStructuresFromPV();
            this.broadcast(this.EVENTS.SELECTED_PV_CHANGED, { id: this._selectedPvId });
        }

        this.broadcast(this.EVENTS.PERFUSED_VOLUME_REMOVED, { id });
    }

    /**
     * Replace a perfused volume by removing the old one and creating a new one.
     *
     * This produces a new unique ID, which naturally invalidates any linked
     * dosimetry data (the PERFUSED_VOLUME_REMOVED event triggers server-side cleanup).
     * The new PV is inserted at the same position in the list.
     *
     * @returns The newly created perfused volume
     */
    replacePerfusedVolume(
        id: string,
        update: { segmentIndices: number[]; tumorIndices: number[]; name?: string }
    ): IPerfusedVolume {
        const index = this._perfusedVolumes.findIndex((pv) => pv.id === id);
        const wasSelected = this._selectedPvId === id;

        // Clean up the old PV (adjusted dose, dose table, TEP volume)
        delete this._adjustedDoses[id];
        delete this._doseTables[id];
        if (id in this._tepVolumes) {
            this._tepVolumes[id].delete();
            delete this._tepVolumes[id];
            this.broadcast(this.EVENTS.TEP_VOLUME_REMOVED, { pvId: id });
        }

        // Build the new PV
        const newId = generatePvId();
        const segmentIndices = [...update.segmentIndices].sort((a, b) => a - b);
        const tumorIndices = [...update.tumorIndices].sort((a, b) => a - b);
        const volumeMl = this.computeUnionVolumeMl(segmentIndices, tumorIndices);
        const tumorVolumeMl = this.computeTumorVolumeMl(tumorIndices);
        const totalLiver = this.getTotalLiverVolumeMl();

        const newPv: IPerfusedVolume = {
            id: newId,
            name: update.name ?? getSmartName(segmentIndices),
            segmentIndices,
            tumorIndices,
            volumeMl,
            tumorVolumeMl,
            hepaticReservePercent: computeHepaticReserve(totalLiver, volumeMl),
        };

        // Replace in-place to preserve list order
        if (index >= 0) {
            this._perfusedVolumes = this._perfusedVolumes.map((pv) => (pv.id === id ? newPv : pv));
        } else {
            this._perfusedVolumes = [...this._perfusedVolumes, newPv];
        }

        // Broadcast removal of old PV (triggers server-side dosimetry cleanup)
        this.broadcast(this.EVENTS.PERFUSED_VOLUME_REMOVED, { id });
        // Broadcast addition of new PV
        this.broadcast(this.EVENTS.PERFUSED_VOLUME_ADDED, { id: newId });

        // Restore selection if the replaced PV was selected
        if (wasSelected) {
            this._selectedPvId = newId;
            this.syncSelectedStructuresFromPV();
            this.broadcast(this.EVENTS.SELECTED_PV_CHANGED, { id: newId });
        }

        return newPv;
    }

    // ========================================================================
    // Selection
    // ========================================================================

    selectPerfusedVolume(id: string | null): void {
        if (this._selectedPvId === id) return;
        this._selectedPvId = id;
        this.syncSelectedStructuresFromPV();
        this.broadcast(this.EVENTS.SELECTED_PV_CHANGED, { id });
    }

    getSelectedPvId(): string | null {
        return this._selectedPvId;
    }

    getSelectedPerfusedVolume(): IPerfusedVolume | undefined {
        return this._perfusedVolumes.find((pv) => pv.id === this._selectedPvId);
    }

    // ========================================================================
    // Highlight (transient UI state — not persisted)
    // ========================================================================

    /** Build a structure key for use in highlight sets. */
    static structureKey(kind: 'segment' | 'tumor', labelValue: number): string {
        return `${kind}:${labelValue}`;
    }

    /** Set the single hovered structure. Pass null to clear hover. */
    setHovered(key: string | null): void {
        if (this._hoveredStructure === key) return;
        this._hoveredStructure = key;
        this.broadcast(this.EVENTS.HIGHLIGHT_CHANGED);
    }

    /** Explicitly set the selected structures (used by PV builder during editing). */
    setSelectedStructures(keys: ReadonlySet<string>): void {
        this._selectedStructures = keys;
        this.broadcast(this.EVENTS.HIGHLIGHT_CHANGED);
    }

    /** Clear both hover and selection. */
    clearHighlight(): void {
        if (this._hoveredStructure === null && this._selectedStructures.size === 0) return;
        this._hoveredStructure = null;
        this._selectedStructures = new Set();
        this.broadcast(this.EVENTS.HIGHLIGHT_CHANGED);
    }

    getHoveredStructure(): string | null {
        return this._hoveredStructure;
    }

    getSelectedStructures(): ReadonlySet<string> {
        return this._selectedStructures;
    }

    /**
     * Sync selected structures from the currently selected perfused volume.
     * Called automatically when the PV selection changes.
     */
    private syncSelectedStructuresFromPV(): void {
        const pv = this.getSelectedPerfusedVolume();
        if (!pv) {
            this._selectedStructures = new Set();
        } else {
            const keys = new Set<string>();
            for (const lv of pv.segmentIndices) keys.add(DosimetryStateService.structureKey('segment', lv));
            for (const lv of pv.tumorIndices) keys.add(DosimetryStateService.structureKey('tumor', lv));
            this._selectedStructures = keys;
        }
        this.broadcast(this.EVENTS.HIGHLIGHT_CHANGED);
    }

    // ========================================================================
    // Dose Adjustments
    // ========================================================================

    setAdjustedDose(pvId: string, injectedActivityGBq: number): void {
        this._adjustedDoses[pvId] = { injectedActivityGBq };
        this.broadcast(this.EVENTS.DOSE_ADJUSTED, { pvId, injectedActivityGBq });
    }

    getAdjustedDose(pvId: string): { injectedActivityGBq: number } | undefined {
        return this._adjustedDoses[pvId];
    }

    clearAdjustedDose(pvId: string): void {
        if (!(pvId in this._adjustedDoses)) return;
        delete this._adjustedDoses[pvId];
        this.broadcast(this.EVENTS.DOSE_ADJUSTED, { pvId, injectedActivityGBq: null });
    }

    // ========================================================================
    // Dose Tables (from API results)
    // ========================================================================

    setDoseTable(pvId: string, table: IDoseTable): void {
        this._doseTables[pvId] = table;
        this.broadcast(this.EVENTS.DOSE_TABLE_LOADED, { pvId });
    }

    getDoseTable(pvId: string): IDoseTable | undefined {
        return this._doseTables[pvId];
    }

    getAllDoseTables(): Readonly<Record<string, IDoseTable>> {
        return this._doseTables;
    }

    // ========================================================================
    // TEP Volumes (from processing job results)
    // ========================================================================

    setTepVolume(pvId: string, imageData: vtkImageData): void {
        this._tepVolumes[pvId] = imageData;
        this.broadcast(this.EVENTS.TEP_VOLUME_LOADED, { pvId });
    }

    getTepVolume(pvId: string): vtkImageData | undefined {
        return this._tepVolumes[pvId];
    }

    removeTepVolume(pvId: string): void {
        if (!(pvId in this._tepVolumes)) return;
        this._tepVolumes[pvId].delete();
        delete this._tepVolumes[pvId];
        this.broadcast(this.EVENTS.TEP_VOLUME_REMOVED, { pvId });
    }

    getSelectedTepVolume(): vtkImageData | null {
        if (!this._selectedPvId) return null;
        return this._tepVolumes[this._selectedPvId] ?? null;
    }

    // ========================================================================
    // Getters
    // ========================================================================

    getPerfusedVolumes(): readonly IPerfusedVolume[] {
        return this._perfusedVolumes;
    }

    getPerfusedVolume(id: string): IPerfusedVolume | undefined {
        return this._perfusedVolumes.find((pv) => pv.id === id);
    }

    getSegmentVolumes(): ReadonlyMap<number, number> {
        return this._segmentVolumes;
    }

    getTumorVolumes(): ReadonlyMap<number, number> {
        return this._tumorVolumes;
    }

    getTumorSegmentOverlaps(): TumorSegmentOverlaps {
        return this._tumorSegmentOverlaps;
    }

    /**
     * Get segment volumes as sorted entries for display.
     */
    getSegmentEntries(): SegmentEntry[] {
        return Array.from(this._segmentVolumes.entries())
            .sort(([a], [b]) => a - b)
            .map(([lv, vol]) => ({
                labelValue: lv,
                name: `Segment ${lv}`,
                volumeMl: vol,
            }));
    }

    /**
     * Get tumor volumes as sorted entries for display.
     */
    getTumorEntries(): SegmentEntry[] {
        return Array.from(this._tumorVolumes.entries())
            .sort(([a], [b]) => a - b)
            .map(([lv, vol]) => ({
                labelValue: lv,
                name: `Tumor ${lv}`,
                volumeMl: vol,
            }));
    }

    // ========================================================================
    // Serialization
    // ========================================================================

    serializeState(): IDosimetryStateDTO {
        return {
            version: 1,
            perfusedVolumes: this._perfusedVolumes,
            adjustedDoses: { ...this._adjustedDoses },
        };
    }

    loadState(dto: IDosimetryStateDTO): void {
        this._perfusedVolumes = [...dto.perfusedVolumes];
        this._adjustedDoses = { ...dto.adjustedDoses };
        this._selectedPvId = this._perfusedVolumes[0]?.id ?? null;
        this.syncSelectedStructuresFromPV();
        this.broadcast(this.EVENTS.INITIALIZED);
    }

    // ========================================================================
    // Reset
    // ========================================================================

    override reset(): void {
        this._initialized = false;
        this._perfusedVolumes = [];
        this._adjustedDoses = {};
        this._doseTables = {};
        for (const tepVolume of Object.values(this._tepVolumes)) {
            tepVolume.delete();
        }
        this._tepVolumes = {};
        this._selectedPvId = null;
        this._segmentVolumes = new Map();
        this._tumorVolumes = new Map();
        this._tumorSegmentOverlaps = new Map();
        this._hoveredStructure = null;
        this._selectedStructures = new Set();
        super.reset();
    }
}
